import { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { getRequest } from './functions';

// Custom option component for extended mode
const ExtendedOption = ({ innerProps, data, isFocused, isSelected }) => {
  const bgColor = isSelected ? '#2684FF' : isFocused ? '#DEEBFF' : 'white';
  const textColor = isSelected ? 'white' : 'inherit';

  return (
    <div
      {...innerProps}
      style={{
        padding: '8px 12px',
        backgroundColor: bgColor,
        color: textColor,
        cursor: 'pointer',
        borderBottom: '1px solid #eee'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, marginBottom: '2px' }}>
            #{data.productId} - {data.productName}
            {data.discontinued && <span style={{ marginLeft: '6px', color: isSelected ? '#ffcdd2' : '#d32f2f' }}>discontinued</span>}
          </div>
          <div style={{ fontSize: '12px', color: isSelected ? 'rgba(255,255,255,0.8)' : '#666' }}>
            {data.productRef && <span style={{ marginRight: '8px' }}>{data.productRef}</span>}
            {data.supplierName && <span>{data.supplierName}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', marginLeft: '12px' }}>
          {data.hasBom && data.rolledUpCost != null ? (
            <div>
              <div style={{ fontWeight: 500, fontSize: '13px' }}>€{data.rolledUpCost.toFixed(2)}</div>
              <div style={{ fontSize: '10px', color: isSelected ? 'rgba(255,255,255,0.7)' : '#999' }}>BOM roll-up</div>
            </div>
          ) : (
            <div style={{ fontWeight: 500, fontSize: '13px' }}>{data.purchasePrice != null ? `€${data.purchasePrice.toFixed(2)}` : '-'}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom single value display for extended mode
const ExtendedSingleValue = ({ data }) => {
  const displayPrice = data.hasBom && data.rolledUpCost != null ? data.rolledUpCost : data.purchasePrice;
  const priceLabel = data.hasBom && data.rolledUpCost != null ? 'BOM' : '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>
        #{data.productId} - {data.productName}
      </span>
      {data.supplierName && <span style={{ color: '#666', fontSize: '12px' }}>({data.supplierName})</span>}
      {displayPrice != null && (
        <span style={{ color: '#666', fontSize: '12px' }}>
          €{displayPrice.toFixed(2)}
          {priceLabel ? ` ${priceLabel}` : ''}
        </span>
      )}
    </div>
  );
};

export default function ProductSelect({ value, onChange, disabled, extended = false }) {
  /*
      value: initial id of the selected product, component will search the label for the given value id
      extended: if true, shows supplier and purchase price in the dropdown
      onChange: callback function to be called when the selected product changes

      returns: a react component that renders a select box with the list of products that is loaded asynchronously
  */

  const [selectValue, setSelectValue] = useState(null);

  useEffect(() => {
    async function fetchData() {
      if (value && !isNaN(Number(value)) && Number.isInteger(Number(value))) {
        const data = await getRequest(`/api/products/${value}`);
        if (data) {
          if (extended) {
            // Fetch supplier name for extended mode
            let supplierName = '';
            if (data.SupplierID) {
              const supplier = await getRequest(`/api/companies/${data.SupplierID}`);
              supplierName = supplier?.NAAM || '';
            }
            setSelectValue({
              value: data.ProductID,
              label: `#${data.ProductID} - ${data.ProductNameNL}`,
              productId: data.ProductID,
              productName: data.ProductNameNL,
              productRef: data.Productref,
              supplierName,
              purchasePrice: data.Eenheidsaankoopprijs,
              hasBom: !!data.HasDetails,
              rolledUpCost: data.RolledUpPurchasePrice,
              discontinued: data.Discontinued
            });
          } else {
            setSelectValue({ value: data.ProductID, label: `#${data.ProductID} - ${data.ProductNameNL}` });
          }
        }
      }
    }
    fetchData();
  }, [value, extended]);

  const loadOptions = async (inputValue, callback) => {
    const trimmedInput = inputValue.trim();
    const isNumeric = /^\d+$/.test(trimmedInput);
    const extendedParam = extended ? '&extended=true' : '';
    let data;

    if (isNumeric) {
      // Search by ProductID
      data = await getRequest(`/api/products/simple/search?q=ProductID:${trimmedInput}${extendedParam}`);
    } else if (extended && trimmedInput.startsWith('@')) {
      // Search by supplier name (prefix with @)
      const supplierSearch = trimmedInput.substring(1);
      data = await getRequest(`/api/products/simple/search?q=SupplierName:${supplierSearch}${extendedParam}`);
    } else if (extended && trimmedInput.startsWith('#')) {
      // Search by product reference (prefix with #)
      const refSearch = trimmedInput.substring(1);
      data = await getRequest(`/api/products/simple/search?q=Productref:${refSearch}${extendedParam}`);
    } else {
      // Default: search by product name
      data = await getRequest(`/api/products/simple/search?q=ProductNameNL:${trimmedInput}${extendedParam}`);
    }

    const options = data.map((product) => {
      if (extended) {
        return {
          value: product.ProductID,
          label: `#${product.ProductID} - ${product.ProductNameNL}`,
          productId: product.ProductID,
          productName: product.ProductNameNL,
          productRef: product.Productref,
          supplierName: product.SupplierName,
          purchasePrice: product.Eenheidsaankoopprijs,
          hasBom: !!product.HasDetails,
          rolledUpCost: product.RolledUpPurchasePrice,
          discontinued: product.Discontinued
        };
      }
      const label = product.Discontinued ? `#${product.ProductID} - ${product.ProductNameNL} discontinued` : `#${product.ProductID} - ${product.ProductNameNL}`;
      return {
        value: product.ProductID,
        label
      };
    });
    callback(options);
  };

  const baseStyles = {
    valueContainer: (base) => ({ ...base, height: extended ? 'auto' : '32px', minHeight: '32px', padding: '2px 8px' }),
    indicatorSeparator: (base) => ({ ...base, height: '32px' }),
    indicatorsContainer: (base) => ({ ...base, height: extended ? 'auto' : '32px', minHeight: '32px' }),
    control: (base) => ({
      ...base,
      height: extended ? 'auto' : '32px',
      minHeight: '32px',
      borderRadius: '8px',
      borderColor: disabled ? '#ebebeb' : '#919191'
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999, width: extended ? 'max(100%, 400px)' : '100%' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: extended ? () => ({}) : undefined // Reset option styles for extended mode (custom component handles it)
  };

  const extendedComponents = extended
    ? {
        Option: ExtendedOption,
        SingleValue: ExtendedSingleValue
      }
    : {};

  return (
    <AsyncSelect
      value={selectValue}
      loadOptions={loadOptions}
      menuPortalTarget={document.body}
      autoComplete="nope"
      inputProps={{ autoComplete: 'nope', 'data-form-type': 'other', 'data-lpignore': 'true' }}
      styles={baseStyles}
      components={extendedComponents}
      defaultOptions
      isClearable
      isDisabled={disabled}
      onChange={(props) => {
        if (props) {
          setSelectValue(props);
          onChange(props.value);
        } else {
          setSelectValue(null);
          onChange(null);
        }
      }}
    />
  );
}
